import { get, post } from "utilities/network";

export type TierProgress = "not_verified" | "pending" | "completed" | "failed";
export type EddStatus = "submitted" | "approved" | "declined";
export type KybStep = "business_details" | "business_documents" | "ubo";
export type ReviewKind = "edd" | "kyb";

export interface ReviewUser {
  id: number;
  firstName: string;
  lastName: string;
  userName: string;
  email: string | null;
  country: string;
  level: number;
}

export interface ReviewAuditLog {
  id: number;
  adminID: number;
  actionType: string;
  reason: string | null;
  timestamp: string;
  actor: { displayName: string } | null;
}

export interface EddSubmission {
  id: number;
  userID: number;
  user: ReviewUser | null;
  attemptNumber: number;
  reviewStatus: EddStatus;
  tier3Status: TierProgress;
  occupation: string;
  sourceOfFunds: string;
  sourceOfWealth: string;
  expectedTransactionVolume: string;
  expectedTransactionFrequency: string;
  natureOfTradingActivity: string;
  submittedAt: string;
  reviewedAt: string | null;
  reviewedByAdminID: number | null;
  declinedReason: string | null;
  reapplicationAvailableAt: string | null;
}

export interface KybOwner {
  id: number;
  fullName: string;
  email: string | null;
  ownershipPercentage: number;
  identityStatus: TierProgress;
  addressStatus: TierProgress;
  isAccountHolder: boolean;
  additionMethod: "invite_email" | "on_device" | null;
  inviteSentAt: string | null;
}

export const kybDocumentLabels = {
  bankStatement: "Bank statement (last 6 months)",
  businessAddressVerification: "Business address verification",
  cacDocument: "Certificate of incorporation",
  statusReport: "Status report",
  memorandumOfArticle: "Memorandum and articles",
};

export interface KybApplication {
  requirements?: {
    country: string;
    registrationNumberLabel: string;
    documents: {
      key: keyof typeof kybDocumentLabels;
      label: string;
      hint: string;
    }[];
  };
  id: number;
  userID: number;
  user: ReviewUser | null;
  tier4Status: TierProgress;
  businessDetailsStatus: TierProgress;
  businessDocumentsStatus: TierProgress;
  uboStatus: TierProgress;
  businessName: string | null;
  cacRegistrationNumber: string | null;
  businessAddress: string | null;
  documents: Record<keyof typeof kybDocumentLabels, string | null>;
  owners: KybOwner[];
  createdAt: string;
  updatedAt: string;
}

export interface KybDecision {
  id: number;
  action: "approved" | "rejected";
  targetType: KybStep | "ubo_owner";
  uboOwnerID: number | null;
  reason: string | null;
  adminID: number;
  createdAt: string;
}

export type EddDetail = EddSubmission & { auditLogs: ReviewAuditLog[] };
export type KybDetail = KybApplication & {
  decisions: KybDecision[];
  auditLogs: ReviewAuditLog[];
};
export interface ReviewList<T> {
  results: T[];
  meta: {
    currentPage: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export const fetchEddReviews = (
  page: number,
  status: EddStatus,
  limit = 25,
  signal?: AbortSignal,
): Promise<ReviewList<EddSubmission>> =>
  get(
    `admin/users/edd/submissions?page=${page}&limit=${limit}&reviewStatus=${status}`,
    signal,
  );

export const fetchKybReviews = (
  page: number,
  status: TierProgress,
  limit = 25,
  signal?: AbortSignal,
): Promise<ReviewList<KybApplication>> =>
  get(
    `admin/users/kyb/submissions?page=${page}&limit=${limit}&stepStatus=${status}`,
    signal,
  );

export const fetchEddReview = (
  userID: string,
  signal?: AbortSignal,
): Promise<EddDetail> =>
  get(`admin/users/${encodeURIComponent(userID)}/edd`, signal);

export const fetchKybReview = (
  userID: string,
  signal?: AbortSignal,
): Promise<KybDetail> =>
  get(`admin/users/${encodeURIComponent(userID)}/kyb`, signal);

// GET already unwraps data; the shared POST helper returns the response envelope.
export const decideEdd = async (
  userID: string,
  action: "approve" | "deny",
  reason: string,
  reapplicationAvailableAt?: string,
): Promise<EddDetail> =>
  (
    await post(
      `admin/users/${encodeURIComponent(userID)}/edd/${action}`,
      action === "approve"
        ? { comment: reason }
        : {
            reason,
            ...(reapplicationAvailableAt ? { reapplicationAvailableAt } : {}),
          },
    )
  ).data;

export const decideKybStep = async (
  userID: string,
  step: KybStep,
  action: "approve" | "reject",
  reason: string,
): Promise<KybDetail> =>
  (
    await post(
      `admin/users/${encodeURIComponent(userID)}/kyb/steps/${step}/${action}`,
      action === "approve" ? { comment: reason } : { reason },
    )
  ).data;

export const rejectKybOwner = async (
  userID: string,
  ownerID: number,
  reason: string,
): Promise<KybDetail> =>
  (
    await post(
      `admin/users/${encodeURIComponent(userID)}/kyb/owners/${ownerID}/reject`,
      { reason },
    )
  ).data;
